import { useState } from 'react';
import { line, Color, Size, dealBudgetProblem, formatDateTime, sameDealBudget, t, type DealBudgetPair } from '@bms2/shared';
import { Button, NumericInput } from '../../components/controls';
import { Icon } from '../../components/Icon';
import { Money } from '../../components/Money';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { PAGES_PATH, put } from './api';
import styles from './DealBudget.module.css';

/** One saved pair as the server sends it (decision 0664). */
export interface BudgetRevisionView {
  id: string;
  targetCents: number;
  maximumCents: number | null;
  setAt: string;
  setByName: string | null;
}

/** The Budget row as the Deal page answers it (decision 0664). */
export interface DealBudgetView {
  current: DealBudgetPair | null;
  history: readonly BudgetRevisionView[];
  /** A note typed on the row before it changed shape, read beneath the inputs. */
  typedNote: string | null;
  /** False for a viewer refused write on the Deal module. */
  mayEdit: boolean;
}

/** The pair is written under the Deal, on the record pages' own root, beside the authority list. */
export const dealBudgetPath = (dealId: string) => `${PAGES_PATH}/deals/${dealId}/budget`;

/**
 * 🔒 The Intent card's Budget row (decision 0664): a Target and a Maximum, both
 * money, saved together by the row's own Save, with every pair saved listed
 * beneath, newest first, each with who saved it and when.
 *
 * 🔨 A Maximum below the Target is refused by name before anything is sent, and
 * the server refuses it again whatever is sent. 🔨 A viewer who may not edit the
 * Deal reads the pair and the history and is drawn no input.
 *
 * The inputs start from the standing pair; the card keys this block to that pair,
 * so a save the page reloads after starts the inputs from what was saved.
 *
 * 🔒 The history folds under a chevron beneath the inputs, closed when the card
 * opens, with its count on the chevron's line (decision 0719, amending 0664).
 * Product owner, 2026-09-24: *"I like the number entry, under them make history
 * be a dropdown not always shown."* 🔒 Every figure is the Money component over
 * its cents (decision 0717).
 */
export function DealBudget({ dealId, budget, editable, apiBaseUrl = '', onChanged, onError }: {
  dealId: string;
  budget: DealBudgetView;
  editable: boolean;
  apiBaseUrl?: string;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const zone = useViewerZone();
  const format = useDateFormat();
  const [target, setTarget] = useState<number | null>(budget.current?.targetCents ?? null);
  const [maximum, setMaximum] = useState<number | null>(budget.current?.maximumCents ?? null);
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const draft = { targetCents: target, maximumCents: maximum };
  // Nothing typed is not a refusal; a Maximum with no Target, or below it, is.
  const problem = target === null && maximum === null ? null : dealBudgetProblem(draft);
  const changed = target !== null && !sameDealBudget(budget.current, { targetCents: target, maximumCents: maximum });
  const save = async () => {
    if (problem || target === null) return;
    setSaving(true);
    const answer = await put(dealBudgetPath(dealId), { targetCents: target, maximumCents: maximum }, apiBaseUrl);
    setSaving(false);
    if (!answer.ok) return onError(answer.message);
    onChanged();
  };
  const canEdit = editable && budget.mayEdit;
  return (
    <div className={styles.block} data-deal-budget={canEdit ? 'edit' : 'read'}>
      {canEdit ? (
        <div className={styles.inputs}>
          <NumericInput size={Size.Medium} label={t('deal.budget.target')} kind="currency" value={target} onChange={setTarget} name="budget-target" required error={problem !== null && target === null ? problem : null} />
          <NumericInput size={Size.Medium} label={t('deal.budget.maximum')} kind="currency" value={maximum} onChange={setMaximum} name="budget-maximum" error={problem !== null && target !== null ? problem : null} />
          <span className={styles.save} data-deal-budget-action="save">
            <Button size={Size.Small} color={Color.Navy} text={[t('deal.budget.save')]} actions={[() => void save()]} disabled={saving || !changed || problem !== null} />
          </span>
        </div>
      ) : (
        <p className={styles.standing} data-deal-budget-standing="true">
          {budget.current ? <BudgetPair pair={budget.current} /> : t('deal.budget.none')}
        </p>
      )}
      {budget.typedNote ? (
        <div className={styles.typed} data-deal-budget-typed-note="true">
          <p className={styles.label}>{t('deal.budget.earlierNote')}</p>
          <p className={styles.note}>{budget.typedNote}</p>
        </div>
      ) : null}
      {budget.history.length > 0 ? (
        <div className={styles.history} data-deal-budget-history={String(budget.history.length)} data-deal-budget-history-open={historyOpen ? 'true' : 'false'}>
          <button type="button" className={styles.fold} aria-expanded={historyOpen ? 'true' : 'false'} onClick={() => setHistoryOpen((was) => !was)} data-deal-budget-history-fold="true">
            {line(t('deal.budget.history'), `(${budget.history.length})`)}
            <Icon meaning={historyOpen ? 'collapse' : 'expand'} size="inline" />
          </button>
          {historyOpen ? (
            <ul className={styles.rows}>
              {budget.history.map((row) => (
                <li key={row.id} className={styles.row} data-deal-budget-revision={row.id}>
                  <span className={styles.pair}><BudgetPair pair={row} /></span>
                  <span className={styles.fact}>{line(t('deal.budget.by'), row.setByName ?? t('deal.budget.unknownPerson'), '·', formatDateTime(row.setAt, zone, format))}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** 🔒 One saved pair as a person reads it: Target and, where one was saved, Maximum, each the Money component over its cents (decision 0717). */
function BudgetPair({ pair }: { pair: DealBudgetPair }) {
  return (
    <>
      {t('deal.budget.target')} <Money cents={pair.targetCents} />
      {pair.maximumCents === null ? null : <> {line('·', t('deal.budget.maximum'))} <Money cents={pair.maximumCents} /></>}
    </>
  );
}
